import { BlockquoteIcon, BoldIcon, ItalicIcon, LinkIcon, UnderlineIcon, BulletedListIcon } from "../icons";
import { EditorInlineToolbar, EditorInlineTooltip, InlineToolbarSeparator, ToolbarToggleButton } from "../toolbar";
import React from "react";
import { isBlockActive, isInlineStyleActive, isSelectingEntity } from "../utils";

import EditorEntityHandling from "../EditorEntityHandling";
import EditorSelectionCoordinates from "../EditorSelectionCoordinates";
import { EditorState } from "draft-js";
import LinkInput from "../toolbar/LinkInput";
import PropTypes from "prop-types";
import { Toggle } from "react-powerplug";
import conditionalTestId from "../conditionalTestId";

export default function StandardInlineToolbar({ editorState, focusEditor, onChange }) {
  const currentSelection = editorState.getSelection();

  return (
    <Toggle initial={false}>
      {({ on, toggle }) => (
        <>
          {on ? (
            <EditorSelectionCoordinates ignoreSelectionFocus={true} editorSelection={currentSelection}>
              {({ innerRef, visible, top, left }) => (
                <EditorInlineTooltip
                  innerRef={innerRef}
                  visible={visible}
                  position={{
                    top,
                    left,
                  }}
                >
                  <EditorEntityHandling editorState={editorState} onChange={onChange}>
                    {({ insertEntity }) => (
                      <LinkInput
                        onInsert={(linkUrl) => {
                          insertEntity("LINK", {
                            url: linkUrl,
                          });

                          setTimeout(focusEditor);
                          toggle();
                        }}
                        onCancel={() => {
                          onChange(EditorState.forceSelection(editorState, currentSelection));
                          toggle();
                        }}
                      />
                    )}
                  </EditorEntityHandling>
                </EditorInlineTooltip>
              )}
            </EditorSelectionCoordinates>
          ) : null}

          {on ? null : (
            <EditorSelectionCoordinates editorSelection={currentSelection}>
              {({ innerRef, visible, top, left }) => (
                <EditorInlineToolbar
                  innerRef={innerRef}
                  visible={visible}
                  editorState={editorState}
                  onChange={onChange}
                  position={{
                    top,
                    left,
                  }}
                >
                  {({ editorState, toggleBlockType, toggleInlineStyle }) => (
                    <>
                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isInlineStyleActive(editorState, "BOLD"),
                          "StandardInlineToolbar/toggle/bold/active",
                          "StandardInlineToolbar/toggle/bold",
                        )}
                        onClick={toggleInlineStyle("BOLD")}
                        isActive={isInlineStyleActive(editorState, "BOLD")}
                      >
                        <BoldIcon />
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isInlineStyleActive(editorState, "ITALIC"),
                          "StandardInlineToolbar/toggle/italic/active",
                          "StandardInlineToolbar/toggle/italic",
                        )}
                        onClick={toggleInlineStyle("ITALIC")}
                        isActive={isInlineStyleActive(editorState, "ITALIC")}
                      >
                        <ItalicIcon />
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isInlineStyleActive(editorState, "UNDERLINE"),
                          "StandardInlineToolbar/toggle/underline/active",
                          "StandardInlineToolbar/toggle/underline",
                        )}
                        onClick={toggleInlineStyle("UNDERLINE")}
                        isActive={isInlineStyleActive(editorState, "UNDERLINE")}
                      >
                        <UnderlineIcon />
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <EditorEntityHandling editorState={editorState} onChange={onChange}>
                        {({ removeEntity }) => {
                          const isActive = isSelectingEntity(editorState, "LINK");

                          return (
                            <ToolbarToggleButton
                              {...conditionalTestId(
                                isActive,
                                "StandardInlineToolbar/toggle/link/active",
                                "StandardInlineToolbar/toggle/link",
                              )}
                              onClick={isActive ? removeEntity : toggle}
                              isActive={isActive}
                            >
                              <LinkIcon />
                            </ToolbarToggleButton>
                          );
                        }}
                      </EditorEntityHandling>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isBlockActive(editorState, "header-three"),
                          "StandardInlineToolbar/toggle/header-three/active",
                          "StandardInlineToolbar/toggle/header-three",
                        )}
                        onClick={toggleBlockType("header-three")}
                        isActive={isBlockActive(editorState, "header-three")}
                      >
                        <span
                          style={{
                            fontSize: 13,
                          }}
                        >
                          H3
                        </span>
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isBlockActive(editorState, "header-four"),
                          "StandardInlineToolbar/toggle/header-four/active",
                          "StandardInlineToolbar/toggle/header-four",
                        )}
                        onClick={toggleBlockType("header-four")}
                        isActive={isBlockActive(editorState, "header-four")}
                      >
                        <span
                          style={{
                            fontSize: 13,
                          }}
                        >
                          H4
                        </span>
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isBlockActive(editorState, "blockquote"),
                          "StandardInlineToolbar/toggle/blockquote/active",
                          "StandardInlineToolbar/toggle/blockquote",
                        )}
                        onClick={toggleBlockType("blockquote")}
                        isActive={isBlockActive(editorState, "blockquote")}
                      >
                        <BlockquoteIcon />
                      </ToolbarToggleButton>

                      <InlineToolbarSeparator />

                      <ToolbarToggleButton
                        {...conditionalTestId(
                          isBlockActive(editorState, "unordered-list-item"),
                          "StandardInlineToolbar/toggle/unordered-list-item/active",
                          "StandardInlineToolbar/toggle/unordered-list-item",
                        )}
                        onClick={toggleBlockType("unordered-list-item")}
                        isActive={isBlockActive(editorState, "unordered-list-item")}
                      >
                        <BulletedListIcon />
                      </ToolbarToggleButton>
                    </>
                  )}
                </EditorInlineToolbar>
              )}
            </EditorSelectionCoordinates>
          )}
        </>
      )}
    </Toggle>
  );
}

StandardInlineToolbar.propTypes = {
  editorState: PropTypes.object.isRequired,
  focusEditor: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
};
